import React, { useEffect, useRef } from 'react';
import { useBattle } from '../context/BattleProvider';
import { useLiveKitRoom } from '../hooks/useLiveKitRoom';
import { ParticipantTile } from '../components/ParticipantTile';
import { BattleChat } from '../components/BattleChat';
import { Track } from 'livekit-client';

// Participant view: see all other participants, hear the backing track, speak/rap.
export const ParticipantView: React.FC = () => {
  const { session, livekitToken, livekitUrl } = useBattle();
  const localVideoRef = useRef<HTMLVideoElement>(null);

  const { isConnected, remoteParticipants, room } = useLiveKitRoom({
    url: livekitUrl,
    token: livekitToken,
  });

  // Enable local camera + mic once connected
  useEffect(() => {
    if (!isConnected || !room) return;
    room.localParticipant.enableCameraAndMicrophone().catch(console.error);
  }, [isConnected, room]);

  // Attach local video preview
  useEffect(() => {
    if (!room || !localVideoRef.current) return;
    const cameraPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (cameraPub?.track) {
      cameraPub.track.attach(localVideoRef.current);
    }
  }, [room, isConnected]);

  const pinnedParticipant = remoteParticipants.find(
    (p) => p.identity === session?.pinned_participant
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 280px',
        gap: 16,
        height: 'calc(100vh - 60px)',
        padding: 20,
        boxSizing: 'border-box',
      }}
    >
      {/* ── LEFT: stage ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800 }}>
            {session?.title}
          </h2>
          {session?.status === 'live' && (
            <span
              className="pill"
              style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}
            >
              LIVE
            </span>
          )}
        </div>

        {/* Main spotlight — pinned participant or placeholder */}
        <div
          style={{
            background: '#000',
            borderRadius: 'var(--radius-lg)',
            border: '2px solid var(--accent)',
            overflow: 'hidden',
            aspectRatio: '16/9',
          }}
        >
          {pinnedParticipant ? (
            <ParticipantTile
              participant={pinnedParticipant}
              isPinned
              isHost={false}
              onPin={() => {}}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
                fontSize: 14,
              }}
            >
              Waiting for host to spotlight a participant…
            </div>
          )}
        </div>

        {/* Participant grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 8,
          }}
        >
          {/* Local self-preview */}
          <div
            style={{
              position: 'relative',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              aspectRatio: '16/9',
            }}
          >
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 6,
                left: 6,
                background: 'rgba(0,0,0,0.6)',
                borderRadius: 4,
                padding: '2px 7px',
                fontSize: 11,
                color: '#fff',
              }}
            >
              You
            </div>
          </div>

          {remoteParticipants
            .filter((p) => p.identity !== session?.pinned_participant)
            .map((p) => (
              <ParticipantTile
                key={p.identity}
                participant={p}
                isPinned={false}
                isHost={false}
                onPin={() => {}}
              />
            ))}
        </div>
      </div>

      {/* ── RIGHT: chat ── */}
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <BattleChat />
      </div>
    </div>
  );
};
